import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  social_handle: "",
  profession: "",
  topic: "",
  why_guest: "",
  availability: "",
};

export default function BeAGuest() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/guest-applications", form);
      toast.success("Application received. We'll be in touch.");
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="be-a-guest-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative">
      <StudioBackdrop opacity={0.12} size={240} />
      <div className="absolute top-32 right-0 w-96 h-96 halftone opacity-20 pointer-events-none hidden md:block" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.25em] uppercase font-bold mb-3">
              <span className="text-[#1E6FE0]">His Story.</span>{" "}
              <span className="text-[#E10600]">Her Story.</span>
            </p>
            <h1 className="font-display text-6xl md:text-7xl uppercase tracking-tight leading-[0.9]">
              Be A <span className="text-[#E10600]">Guest</span>
            </h1>
            <p className="mt-6 text-zinc-400">
              We feature real voices from both sides of the conversation —
              relationship coaches, fathers, single women, marriage counselors, dating
              creators, and anyone living the dynamics we discuss. If you've got a
              point to prove, this is the table.
            </p>

            <div className="mt-10 space-y-4 border-t border-[#262626] pt-8">
                <Step n="01" title="Submit Application" body="Fill out the form. Be specific about your perspective on relationships, dating, or intersexual dynamics." />
                <Step n="02" title="Discovery Call" body="If we vibe, we'll set up a 20-min call to align on the conversation." />
                <Step n="03" title="Record" body="Studio sessions in New York City or Boston — or remote via Riverside. ~75 min runtime." />
                <Step n="04" title="Promote" body="Episode drops Monday 7PM. We push it across all channels — you bring your audience." />
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="guest-success"
                className="border border-[#E10600] p-10 bg-[#121212] text-center"
              >
                <CheckCircle2 className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                <h2 className="font-display text-4xl uppercase">Application Received</h2>
                <p className="mt-4 text-zinc-400">
                  Manuel's team reviews every submission personally. Expect to hear back
                  within 5–7 business days.
                </p>
                <button
                  data-testid="guest-submit-another"
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary mt-8"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form
                data-testid="guest-application-form"
                onSubmit={submit}
                className="border border-[#262626] bg-[#0F0F0F] p-6 md:p-10 space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Full Name *" required>
                    <input data-testid="guest-input-full-name" required value={form.full_name} onChange={update("full_name")} className="input-edge" placeholder="Manuel Santana" />
                  </Field>
                  <Field label="Email *" required>
                    <input data-testid="guest-input-email" required type="email" value={form.email} onChange={update("email")} className="input-edge" placeholder="you@example.com" />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="guest-input-phone" value={form.phone} onChange={update("phone")} className="input-edge" placeholder="(555) 555-5555" />
                  </Field>
                  <Field label="Social Handle">
                    <input data-testid="guest-input-social" value={form.social_handle} onChange={update("social_handle")} className="input-edge" placeholder="@yourhandle" />
                  </Field>
                </div>

                <Field label="Profession / What You Do *" required>
                  <input data-testid="guest-input-profession" required value={form.profession} onChange={update("profession")} className="input-edge" placeholder="Founder · Author · Producer" />
                </Field>

                <Field label="What's Your Point? (Topic / Angle) *" required>
                  <input data-testid="guest-input-topic" required value={form.topic} onChange={update("topic")} className="input-edge" placeholder="Dating · Marriage · Masculine Polarity · Modern Love…" />
                </Field>

                <Field label="Why You? *" required>
                  <textarea
                    data-testid="guest-input-why"
                    required
                    rows={4}
                    value={form.why_guest}
                    onChange={update("why_guest")}
                    className="input-edge resize-none"
                    placeholder="What perspective on relationships do you bring that no one else can?"
                  />
                </Field>

                <Field label="Availability">
                  <input data-testid="guest-input-availability" value={form.availability} onChange={update("availability")} className="input-edge" placeholder="Weekday evenings EST" />
                </Field>

                <div className="pt-4 flex flex-wrap items-center gap-4 justify-between">
                  <p className="text-xs text-zinc-500 font-mono">* Required fields</p>
                  <button
                    data-testid="guest-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Submit Application"}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="flex gap-4">
      <span className="font-display text-3xl text-[#E10600] leading-none w-12 shrink-0">{n}</span>
      <div>
        <p className="font-display text-xl uppercase">{title}</p>
        <p className="text-sm text-zinc-400 mt-1">{body}</p>
      </div>
    </div>
  );
}
